<template>
  <BaseModal v-model:is-open="isOpen">
    <template #title>
      代理ログイン選択
    </template>
    <template #contents>
      <div>
        <label for="organization">支社名</label>
        <v-select
          id="organization"
          v-model="selectedOrganization"
          :items="filteredOrganizations"
          item-title="organizationName"
          item-value="organizationCd"
          return-object
          color="primary"
          variant="outlined"
          :class="isOrganizationDisabled ? 'bg-readonly-form' : ''"
          :disabled="isOrganizationDisabled"
          placeholder="選択してください"
          autocomplete="off"
          hide-details
          @update:model-value="onOrganizationChanged"
        />
      </div>
      <div class="mt-4">
        <label for="station">駅名</label>
        <v-select
          id="station"
          v-model="selectedStation"
          :items="filteredStations"
          item-title="stationName"
          :item-value="getStationItemValue"
          return-object
          color="primary"
          variant="outlined"
          :class="isStationDisabled ? 'bg-readonly-form' : ''"
          :disabled="isStationDisabled"
          placeholder="選択してください"
          autocomplete="off"
          hide-details
          @update:model-value="onStationChanged"
        />
      </div>
      <div class="mt-4">
        <label for="userCompany">利用者名</label>
        <v-select
          id="userCompany"
          v-model="selectedUserCompany"
          :items="filteredUserCompanies"
          item-title="userCompanyName"
          item-value="userCompanyCd"
          return-object
          color="primary"
          variant="outlined"
          :class="isUserCompanyDisabled ? 'bg-readonly-form' : ''"
          :disabled="isUserCompanyDisabled"
          placeholder="選択してください"
          autocomplete="off"
          hide-details
          @update:model-value="onUserCompanyChanged"
        >
          <template #selection="{ item }">
            <div class="user-company-option">
              <div>{{ item.raw.userCompanyName }}</div>
              <div
                v-if="item.raw.userCompanyNameRyaku"
                class="user-company-option__subtitle"
              >
                {{ item.raw.userCompanyNameRyaku }}
              </div>
            </div>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <template #title>
                <div class="user-company-option">
                  <div>{{ item.raw.userCompanyName }}</div>
                  <div
                    v-if="item.raw.userCompanyNameRyaku"
                    class="user-company-option__subtitle"
                  >
                    {{ item.raw.userCompanyNameRyaku }}
                  </div>
                </div>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </div>
      <div class="mt-4">
        <label for="deliveryCompany">集配人名</label>
        <v-select
          id="deliveryCompany"
          v-model="selectedDeliveryCompany"
          v-model:menu="isDeliveryCompanyMenuOpen"
          :items="filteredDeliveryCompanies"
          item-title="deliveryCompanyName"
          item-value="deliveryCompanyCd"
          return-object
          color="primary"
          variant="outlined"
          :class="isDeliveryCompanyDisabled ? 'bg-readonly-form' : ''"
          :disabled="isDeliveryCompanyDisabled"
          placeholder="選択してください"
          autocomplete="off"
          hide-details
          @update:model-value="onDeliveryCompanyChanged"
        >
          <template #prepend-item>
            <!-- 未選択 -->
            <v-list-item
              title=""
              @click="clearDeliveryCompanySelection"
            />
          </template>
        </v-select>
      </div>

      <v-divider thickness="2" class="mt-6" />

      <div class="mt-4">
        <label for="driver">代理ログイン先（ドライバー名）</label>
        <v-select
          id="driver"
          v-model="selectedDriver"
          :items="filteredDrivers"
          item-title="userName"
          item-value="platId"
          return-object
          color="primary"
          variant="outlined"
          :class="isDriverDisabled ? 'bg-readonly-form' : ''"
          :disabled="isDriverDisabled"
          placeholder="選択してください"
          autocomplete="off"
          hide-details
        />
      </div>
    </template>
    <template #actionButtons>
      <CustomButton
        :disabled="isActionButtonDisabled"
        @click="proxyLogin"
      >
        決定
      </CustomButton>
      <CustomButton
        class="mt-4"
        variant="outlined"
        @click="onCanceled"
      >
        キャンセル
      </CustomButton>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import BaseModal from '../base/BaseModal.vue';
import CustomButton from '~/components/common/parts/CustomButton.vue';
import { getStationItemValue } from '~/utils/selectItemValueUtils';

const DISPLAY_ID = 'C-08';

const emits = defineEmits(['completed', 'canceled']);

const {
  isOpen,
  filteredOrganizations,
  filteredStations,
  filteredUserCompanies,
  filteredDeliveryCompanies,
  filteredDrivers,
  isOrganizationDisabled,
  isStationDisabled,
  isUserCompanyDisabled,
  isDeliveryCompanyDisabled,
  isDriverDisabled,
  isActionButtonDisabled,
  selectedOrganization,
  selectedStation,
  selectedUserCompany,
  selectedDeliveryCompany,
  selectedDriver,
  isDeliveryCompanyMenuOpen,
  open,
  close,
  onOrganizationChanged,
  onStationChanged,
  onUserCompanyChanged,
  onDeliveryCompanyChanged,
  clearDeliveryCompanySelection,
  proxyLogin,
  onCanceled,
} = useProxyLoginModalLogic(DISPLAY_ID, emits);

defineExpose({
  open,
  close,
});
</script>

<style scoped>
label {
  font-size: 14px;
  font-weight: bold;
}

.user-company-option {
  padding: 2px 0;
  line-height: 1.4;
}

.user-company-option__subtitle {
  padding-left: 1.125rem;
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.72;
}
</style>
